import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Layout, Mail, ArrowRight, KeyRound, BarChart3, FileText, Ban, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fireConfetti() {
  const end = Date.now() + 1800;
  const colors = ["#6366f1", "#3b82f6", "#10b981", "#f59e0b", "#ec4899"];
  (function frame() {
    confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors });
    confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const packageId = searchParams.get("package");
  const confettiFired = useRef(false);

  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const [accountInfo, setAccountInfo] = useState({ account_email: "", needs_password_setup: false });
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const pollStatus = useCallback(async () => {
    if (!sessionId || attempts >= 15) {
      if (attempts >= 15) setStatus("failed");
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setStatus("paid");
        setAccountInfo({
          account_email: res.data.account_email || "",
          needs_password_setup: !!res.data.needs_password_setup,
        });
        return;
      }
      if (res.data.status === "expired") {
        setStatus("expired");
        return;
      }
      setAttempts(prev => prev + 1);
      setTimeout(() => pollStatus(), 2000);
    } catch {
      setAttempts(prev => prev + 1);
      setTimeout(() => pollStatus(), 2000);
    }
  }, [sessionId, attempts]);

  useEffect(() => { pollStatus(); }, []);

  useEffect(() => {
    if (status === "paid" && !confettiFired.current) {
      confettiFired.current = true;
      fireConfetti();
    }
  }, [status]);

  const resendSetupEmail = async () => {
    if (!pageId) return;
    setResending(true);
    try {
      await axios.post(`${API}/pages/${pageId}/resend-setup-email`);
      setResent(true);
    } catch {
      setResent(true);
    } finally {
      setResending(false);
    }
  };

  const pageId = searchParams.get("page_id") || "";
  const packageNames = { basis: "Starter", pro: "Pro", premium: "Premium" };
  const packagePrices = { basis: "29", pro: "49", premium: "79" };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-slate-50 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        {/* Polling */}
        {status === "polling" && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 md:p-10 text-center">
            <div className="relative w-16 h-16 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full bg-indigo-100 animate-ping opacity-30" />
              <div className="relative w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              </div>
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2" data-testid="payment-polling">
              Zahlung wird verarbeitet...
            </h1>
            <p className="text-slate-500 text-sm">Einen Moment bitte, wir prüfen deine Zahlung.</p>
          </div>
        )}

        {/* Success */}
        {status === "paid" && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden" data-testid="payment-success">
            {/* Success header */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-8 py-8 text-center text-white">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle2 className="w-9 h-9 text-white" />
              </motion.div>
              <h1 className="text-2xl font-bold mb-1">Willkommen bei <span className="text-slate-900">Web</span><span className="text-indigo-600">Generator</span>!</h1>
              <p className="text-white/80 text-sm">Deine Bestellung war erfolgreich</p>
            </div>

            {/* Order details */}
            <div className="p-8">
              <div className="bg-slate-50 rounded-xl p-5 mb-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Paket</span>
                  <span className="font-semibold text-slate-900">{packageNames[packageId] || "Abo"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Preis</span>
                  <span className="font-semibold text-slate-900">{packagePrices[packageId] || "–"} €/Monat</span>
                </div>
                <div className="h-px bg-slate-200" />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Status</span>
                  <span className="font-semibold text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Bezahlt
                  </span>
                </div>
              </div>

              {/* Info about what happens next */}
              <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 mb-6">
                <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900 mb-1">Was passiert jetzt?</p>
                  <p className="text-blue-700 leading-relaxed">
                    Deine Domain wird jetzt eingerichtet. Die DNS-Umstellung braucht etwas Zeit (meist wenige
                    Minuten, in Einzelfällen bis zu 24 Stunden) - in der Regel ist deine Seite aber noch am
                    selben Tag erreichbar. Du erhältst E-Mail-Updates zu jedem Schritt.
                  </p>
                </div>
              </div>

              {/* Account setup CTA */}
              {accountInfo.needs_password_setup && (
                <div data-testid="account-setup-card" className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 rounded-xl p-5 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-indigo-600" />
                    <p className="font-semibold text-indigo-900 text-sm">Erstelle jetzt dein WebGenerator-Konto!</p>
                  </div>
                  <p className="text-indigo-700/80 text-xs mb-4 leading-relaxed">
                    Wir haben dir eine E-Mail an <span className="font-medium">{accountInfo.account_email}</span> geschickt,
                    um dein Passwort festzulegen. Mit deinem Konto kannst du:
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center gap-2 text-xs text-indigo-800">
                      <BarChart3 className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" /> Live-Status deiner Webseite im Dashboard verfolgen
                    </li>
                    <li className="flex items-center gap-2 text-xs text-indigo-800">
                      <FileText className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" /> Content-Updates anfragen & Vertrag einsehen
                    </li>
                    <li className="flex items-center gap-2 text-xs text-indigo-800">
                      <Ban className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" /> Abo jederzeit selbst kündigen
                    </li>
                  </ul>
                  <Button
                    data-testid="resend-setup-email-btn"
                    size="sm"
                    variant="outline"
                    onClick={resendSetupEmail}
                    disabled={resending || resent}
                    className="w-full h-9 rounded-lg border-indigo-300 text-indigo-700 hover:bg-indigo-100 text-xs"
                  >
                    <KeyRound className="w-3.5 h-3.5 mr-1.5" />
                    {resent ? "E-Mail erneut gesendet!" : resending ? "Wird gesendet..." : "Setup-E-Mail erneut senden"}
                  </Button>
                </div>
              )}

              <div className="space-y-3">
                {pageId && (
                  <Button
                    data-testid="goto-dashboard"
                    onClick={() => navigate(`/dashboard/${pageId}`)}
                    className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-base"
                  >
                    <Layout className="w-4 h-4 mr-2" />
                    Zum Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={() => navigate("/")}
                  className="w-full h-10 rounded-xl text-slate-500 hover:text-slate-700 text-sm"
                >
                  Zur Startseite
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Failed / Expired */}
        {(status === "failed" || status === "expired") && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 md:p-10 text-center" data-testid="payment-failed">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-9 h-9 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">
              {status === "expired" ? "Sitzung abgelaufen" : "Zahlung fehlgeschlagen"}
            </h1>
            <p className="text-slate-500 text-sm mb-6">
              Bitte versuche es erneut oder kontaktiere unseren Support.
            </p>
            <Button
              onClick={() => navigate("/")}
              className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700"
            >
              Zurück zur Startseite
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
